import { ref } from 'vue';
import { useAuth } from 'src/boot/auth';
import { useSchoolData, type SemesterOption } from './useSchoolData';
import { toStudentClass, type StudentClass } from './useStudentClasses';
import {
  isCurrentSemester,
  isUpcomingSemester,
  todayLocalISO,
} from 'src/modules/School/utilities/semesterDates';

/**
 * What a student sees on the dashboard: their own enrollments, grouped by semester,
 * with no filter to choose.
 *
 * The semester picker is a teacher's tool — a teacher slices many classes across
 * terms. A student has one or two enrollments, so a filter they never set can only
 * hide their own class: register through a teacher's link for a term that isn't the
 * one the picker defaulted to, and the dashboard reports "not enrolled in any class"
 * for the class they just joined.
 *
 * So this reads every enrollment the student has (the endpoint self-scopes students
 * to their own rows) and shows:
 *   - the current semester, if one contains today
 *   - any future semester the student is actually enrolled in
 * Past semesters are dropped, and a registration is never hidden — that second rule is
 * the whole point, so a future-term enrollment shows up the moment it is made.
 *
 * Future terms the student has NOT joined stay out: "the next one" is a proxy for
 * "soon" and a bad one — with sparse semesters the nearest can be a year away, and it
 * rendered an empty block that told the student nothing.
 */
export interface StudentSemesterGroup {
  semester: SemesterOption;
  /** True for the semester containing today; the rest are upcoming. */
  isCurrent: boolean;
  classes: StudentClass[];
}

export function useStudentSemesterOverview() {
  const { currentUser } = useAuth();
  const { fetchSemesters, fetchStudentEnrollments } = useSchoolData();

  const groups = ref<StudentSemesterGroup[]>([]);
  const loading = ref(false);
  const loaded = ref(false);

  async function fetchOverview(): Promise<void> {
    const studentDocumentId = currentUser.value?.documentId || '';
    if (!studentDocumentId) {
      groups.value = [];
      loaded.value = true;
      return;
    }

    loading.value = true;
    try {
      // No semester and no school argument: everything this student is enrolled in.
      const [semesters, rows] = await Promise.all([
        fetchSemesters(),
        fetchStudentEnrollments(studentDocumentId),
      ]);

      const classes = rows
        .map(toStudentClass)
        .filter((row) => !!row.classDocumentId);

      groups.value = buildSemesterGroups(semesters, classes);
      loaded.value = true;
    } finally {
      loading.value = false;
    }
  }

  return { groups, loading, loaded, fetchOverview };
}

/**
 * Pure selection + grouping, exported for tests: which semesters to show and the
 * classes under each. `semesters` arrives sorted `start_date:desc`.
 */
export function buildSemesterGroups(
  semesters: SemesterOption[],
  classes: StudentClass[],
  today = todayLocalISO(),
): StudentSemesterGroup[] {
  if (!semesters.length) return [];

  const enrolledSemesterIds = new Set(
    classes.map((row) => row.semesterDocumentId).filter(Boolean),
  );

  const current = semesters.find((s) => isCurrentSemester(s, today));

  const selected = new Map<string, SemesterOption>();
  if (current) selected.set(current.documentId, current);
  for (const semester of semesters) {
    if (
      isUpcomingSemester(semester, today) &&
      enrolledSemesterIds.has(semester.documentId)
    ) {
      selected.set(semester.documentId, semester);
    }
  }

  return [...selected.values()]
    .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''))
    .map((semester) => ({
      semester,
      isCurrent: isCurrentSemester(semester, today),
      classes: classes.filter(
        (row) => row.semesterDocumentId === semester.documentId,
      ),
    }));
}
